// SPDX-License-Identifier: GPL-3.0-or-later
// Copyright (C) 2026 Radiooooo

// The application on a page: the landing first, on the foundation, which is
// all a visitor gets; then, when there is a session to show it to, the rest of
// the core and the frame, one chunk imported here, so a visitor downloads none
// of it (decision 0002).

import { createLifetime } from "../core/lifetime";
import type { Core } from "./core";
import type { Foundation } from "./foundation";
import type { Frame } from "./frame";
import { mountLandingScreen } from "./landing";
import { startSession, type Screen } from "./session";

/** The module the frame lives in, with the core's listener half and the playback wiring. Tests hand in the real one to skip the chunk. */
export type FrameModule = {
    createCore: (foundation: Foundation) => Core;
    mountFrame: (root: HTMLElement, core: Core) => Promise<Frame>;
    wirePlayback: (core: Core, frame: Frame, options?: { search?: string }) => () => void;
};

export function startApplication(foundation: Foundation, root: HTMLElement, options: { load?: () => Promise<FrameModule>; search?: string } = {}) {
    const lifetime = createLifetime();
    lifetime.onDestroy(foundation.destroy);
    const landing = mountLandingScreen(root, foundation);
    lifetime.onDestroy(landing.destroy);

    const load = options.load ?? (() => import("./frame"));
    let core: Core | undefined;
    let frame: Frame | undefined;
    let loading: Promise<Frame | undefined> | undefined;
    /** The core's listener half and the frame, fetched and built once; wired for playback as they arrive, whatever the session did meanwhile. */
    const ensure = (): Promise<Frame | undefined> => (loading ??= load()
        .then(async (module) => {
            const built = module.createCore(foundation);
            if (lifetime.signal.aborted) { built.destroy(); throw new Error("destroyed"); }
            core = built;
            lifetime.onDestroy(built.destroy);
            return { module, mounted: await module.mountFrame(root, built) };
        })
        .then(({ module, mounted }) => {
            if (lifetime.signal.aborted) { mounted.destroy(); return undefined; }
            frame = mounted;
            lifetime.onDestroy(mounted.destroy);
            module.wirePlayback(core!, mounted, { search: options.search });
            return mounted;
        })
        .catch((error: unknown) => {
            // a chunk that did not arrive: the next session change asks again
            console.error("frame: could not load", error);
            loading = undefined;
            return undefined;
        }));
    const screen: Screen = { landing, frame: { ensure, current: () => frame } };
    lifetime.onDestroy(startSession(foundation, screen));

    return {
        destroy: lifetime.destroy,
        onDestroy: lifetime.onDestroy,
        /** The core's listener half and the frame, once a session has brought them. */
        core: () => core,
        frame: () => frame,
        /** The frame, fetched now if it is not here yet. */
        ready: ensure,
    };
}
